if (!customElements.get('cn-review-slider')) {
  customElements.define('cn-review-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.viewport = this.querySelector('.cn-mini-reviews__viewport');
      this.track = this.querySelector('.cn-mini-reviews__slides');
      this.slides = [...this.querySelectorAll('[data-cn-slide]')];
      this.dots = [...this.querySelectorAll('[data-cn-to]')];
      if (!this.viewport || !this.track || !this.slides.length) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.index = 0;
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index), options));
      this.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : (this.index + (event.key === 'ArrowRight' ? 1 : -1) + this.slides.length) % this.slides.length;
        this.show(next);
        this.dots[this.index]?.focus({ preventScroll: true });
      }, options);
      this.viewport.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, time: performance.now(), horizontal: false };
      }, options);
      this.viewport.addEventListener('pointermove', (event) => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const dx = event.clientX - this.drag.x;
        const dy = event.clientY - this.drag.y;
        if (!this.drag.horizontal) {
          if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { this.drag = null; return; }
          if (Math.abs(dx) < 8) return;
          this.drag.horizontal = true;
          this.viewport.setPointerCapture(event.pointerId);
          this.classList.add('is-dragging');
        }
        this.drag.dx = dx;
        const atEdge = (this.index === 0 && dx > 0) || (this.index === this.slides.length - 1 && dx < 0);
        this.translate(dx * (atEdge ? 0.25 : 1));
      }, options);
      const release = (event) => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const drag = this.drag;
        this.drag = null;
        this.classList.remove('is-dragging');
        const elapsed = Math.max(1, performance.now() - drag.time);
        const shouldMove = event.type !== 'pointercancel' && drag.horizontal && (Math.abs(drag.dx) > 40 || (Math.abs(drag.dx) > 12 && Math.abs(drag.dx) / elapsed > 0.45));
        this.show(this.index + (shouldMove ? (drag.dx < 0 ? 1 : -1) : 0));
      };
      this.viewport.addEventListener('pointerup', release, options);
      this.viewport.addEventListener('pointercancel', release, options);
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), options);
      this.resize = new ResizeObserver(() => this.updateLayout());
      this.resize.observe(this.viewport);
      this.slides.forEach((slide) => this.resize.observe(slide));
      this.show(0);
    }
    translate(offset = 0) {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = this.slides[0].getBoundingClientRect().width + gap;
      this.track.style.transform = 'translateX(' + (-this.index * step + offset) + 'px)';
    }
    updateLayout() {
      if (!this.slides?.[this.index]) return;
      this.viewport.style.height = this.slides[this.index].offsetHeight + 'px';
      if (!this.drag?.horizontal) this.translate();
    }
    show(index) {
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.slides.forEach((slide, position) => {
        const active = position === this.index;
        slide.hidden = false;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      this.dots.forEach((dot, position) => dot.setAttribute('aria-pressed', String(position === this.index)));
      this.updateLayout();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.abort = null;
      this.drag = null;
    }
  });
}
if (!customElements.get('cn-sticky-cart')) {
  customElements.define('cn-sticky-cart', class extends HTMLElement {
    connectedCallback() {
      this.source = document.querySelector('product-info[data-section="' + CSS.escape(this.dataset.section) + '"]');
      if (!this.source) return;
      this.button = this.querySelector('[data-cn-add]');
      this.variantLabel = this.querySelector('[data-cn-variant]');
      this.priceLabel = this.querySelector('[data-cn-price]');
      try { this.variants = JSON.parse(this.querySelector('[data-cn-variants]').textContent); } catch { this.variants = []; }
      this.abort = new AbortController();
      this.onScroll = () => {
        if (this.scheduled) return;
        this.scheduled = requestAnimationFrame(() => { this.scheduled = null; this.sync(); });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true, signal: this.abort.signal });
      window.addEventListener('resize', this.onScroll, { passive: true, signal: this.abort.signal });
      this.button.addEventListener('click', () => {
        const original = this.originalButton();
        if (!original || original.disabled || original.getAttribute('aria-disabled') === 'true') return;
        original.click();
      }, { signal: this.abort.signal });
      this.observer = new MutationObserver(this.onScroll);
      this.observer.observe(this.source, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
      this.source.addEventListener('change', this.onScroll, { signal: this.abort.signal });
      this.source.addEventListener('cn:bundle-change', this.onScroll, { signal: this.abort.signal });
      this.sync();
    }
    originalButton() {
      return this.source.querySelector('[id^="ProductSubmitButton-"]');
    }
    sync() {
      const original = this.originalButton();
      if (!original) { this.hidden = true; return; }
      this.hidden = original.getBoundingClientRect().bottom >= 0;
      const unavailable = original.disabled || original.getAttribute('aria-disabled') === 'true';
      this.button.disabled = unavailable;
      const label = original.querySelector('span:not(.loading__spinner)');
      if (label && label.textContent.trim()) this.button.textContent = label.textContent.trim();
      const bundle = this.source.querySelector('cn-bundle:not([hidden])');
      const selection = bundle?.getSelection?.();
      if (selection) {
        this.variantLabel.textContent = selection.label;
        this.priceLabel.textContent = selection.total;
        return;
      }
      const form = original.form;
      const selectedId = form?.querySelector('input[name="id"]')?.value;
      const variant = this.variants.find(item => String(item.id) === String(selectedId));
      if (variant) {
        this.variantLabel.textContent = variant.title === 'Default Title' ? '' : variant.title;
        this.priceLabel.textContent = variant.price.replace(/<[^>]*>/g, '');
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      if (this.scheduled) cancelAnimationFrame(this.scheduled);
    }
  });
}
